import { useMemo, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { caseService, judicialService } from "@/services/api";
import { mapDbCaseToFrontend } from "@/services/mapping";

function daysBetween(d1: string, d2: string): number {
  return Math.ceil((new Date(d2).getTime() - new Date(d1).getTime()) / (1000 * 60 * 60 * 24));
}

export default function CalendarioPage() {
  const navigate = useNavigate();
  const todayStr = new Date().toISOString().split("T")[0];

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [cases, setCases] = useState<any[]>([]);
  const [courtRequests, setCourtRequests] = useState<any[]>([]);

  useEffect(() => {
    async function loadData() {
      try {
        setError("");
        const [casesData, judicialData] = await Promise.all([
          caseService.list().catch(() => []),
          judicialService.list().catch(() => [])
        ]);

        if (Array.isArray(casesData)) {
          setCases(casesData.map(mapDbCaseToFrontend));
        }
        if (Array.isArray(judicialData)) {
          setCourtRequests(judicialData);
        }
      } catch (err: any) {
        console.error("Error loading calendar deadlines:", err);
        setError("Error al cargar plazos: " + (err.response?.data?.detail || err.message));
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  // Aggregate all active deadlines (cases and court requests) dynamically
  const plazos = useMemo(() => {
    const list: any[] = [];

    // 1. Add pending court requests
    courtRequests
      .filter((cr) => cr.estado !== "respondido")
      .forEach((cr) => {
        const d = cr.fecha_vencimiento ? daysBetween(todayStr, cr.fecha_vencimiento) : 999;
        list.push({
          id: cr.id,
          title: `Requerimiento Judicial: RIT ${cr.rit_ruc}`,
          subtitle: cr.tribunal,
          studentName: cr.estudiante_nombre || "—",
          fechaLimite: cr.fecha_vencimiento ? new Date(cr.fecha_vencimiento).toISOString().split("T")[0] : "",
          daysRemaining: d,
          caseId: cr.caso_id,
          type: "court"
        });
      });

    // 2. Add open case deadlines
    cases
      .filter((c) => c.estado !== "cerrado" && c.fechaLimite)
      .forEach((c) => {
        const d = daysBetween(todayStr, c.fechaLimite);
        list.push({
          id: c.id,
          title: `Plazo de Caso: ${c.titulo}`,
          subtitle: `Categoría: ${c.categoria}`,
          studentName: c.studentName || "—",
          fechaLimite: c.fechaLimite,
          daysRemaining: d,
          caseId: c.id,
          type: "case"
        });
      });

    return list.sort((a, b) => a.daysRemaining - b.daysRemaining);
  }, [cases, courtRequests, todayStr]);

  const currentYear = new Date().getFullYear();
  const currentMonthName = new Date().toLocaleString("es-CL", { month: "long" }).toUpperCase();
  const currentMonthNum = new Date().getMonth() + 1; // 1-indexed

  // Total days in current month
  const totalDays = new Date(currentYear, currentMonthNum, 0).getDate();
  const dias = Array.from({ length: totalDays }, (_, i) => i + 1);
  const diasSemana = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

  if (loading) {
    return (
      <div className="p-12 text-center flex flex-col items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-3xl text-primary-500 mb-2" />
        <p className="text-sm text-foreground-500">Cargando calendario de plazos...</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-5 animate-fade-in-up">
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-foreground-950">Calendario de Plazos</h1>
        <p className="text-sm text-foreground-500 mt-0.5">Visualización integrada de vencimiento de casos abiertos, solicitudes e informes a tribunales.</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          <i className="ri-error-warning-line mr-1" />{error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Calendar Grid */}
        <div className="lg:col-span-2 bg-white border border-background-200/80 rounded-2xl p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-foreground-900 capitalize">{currentMonthName} {currentYear}</h3>
            <div className="flex items-center gap-1">
              <button className="w-8 h-8 rounded-lg hover:bg-background-50 flex items-center justify-center cursor-pointer border border-background-200">
                <i className="ri-arrow-left-s-line text-foreground-500" />
              </button>
              <button className="w-8 h-8 rounded-lg hover:bg-background-50 flex items-center justify-center cursor-pointer border border-background-200">
                <i className="ri-arrow-right-s-line text-foreground-500" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-1">
            {diasSemana.map((d) => (
              <div key={d} className="text-center text-xs font-semibold text-foreground-400 py-2">{d}</div>
            ))}
            {dias.map((dia) => {
              const today = new Date();
              const isToday = today.getDate() === dia && (today.getMonth() + 1) === currentMonthNum;
              
              // Find matching deadlines on this specific calendar day
              const dayDeadlines = plazos.filter((p) => {
                if (!p.fechaLimite) return false;
                const parts = p.fechaLimite.split("-").map(Number);
                return parts[1] === currentMonthNum && parts[2] === dia;
              });

              const hasDeadline = dayDeadlines.length > 0;
              const hasOverdue = dayDeadlines.some(p => p.daysRemaining < 0);

              const handleClick = () => {
                if (dayDeadlines.length > 0) {
                  const targetCaseId = dayDeadlines[0].caseId;
                  if (targetCaseId) {
                    navigate(`/casos/${targetCaseId}`);
                  }
                }
              };

              return (
                <div
                  key={dia}
                  onClick={handleClick}
                  className={`text-center py-2 px-1 rounded-xl text-sm transition-all duration-200 ${
                    isToday
                      ? "bg-primary-500 text-white font-bold shadow-md scale-105"
                      : hasDeadline
                      ? hasOverdue 
                        ? "bg-red-50 text-red-700 font-semibold cursor-pointer hover:bg-red-100 border border-red-200" 
                        : "bg-amber-50 text-amber-700 font-semibold cursor-pointer hover:bg-amber-100 border border-amber-200"
                      : "text-foreground-600 hover:bg-background-50"
                  }`}
                  title={hasDeadline ? dayDeadlines.map(d => `${d.title} (${d.studentName})`).join("\n") : undefined}
                >
                  <span className="block">{dia}</span>
                  {hasDeadline && (
                    <div className="flex gap-0.5 justify-center mt-0.5">
                      {dayDeadlines.map((p, idx) => (
                        <span 
                          key={p.id || idx} 
                          className={`w-1.5 h-1.5 rounded-full ${
                            isToday 
                              ? "bg-white/80" 
                              : p.daysRemaining < 0 
                              ? "bg-red-500 animate-pulse" 
                              : "bg-amber-500"
                          }`} 
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Upcoming Deadlines Sidebar */}
        <div className="bg-white border border-background-200/80 rounded-2xl p-5 shadow-sm space-y-4">
          <h3 className="text-sm font-semibold text-foreground-900 uppercase tracking-wider">Próximos Vencimientos</h3>
          <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
            {plazos.map((p) => {
              const overdue = p.daysRemaining < 0;
              return (
                <div
                  key={p.id}
                  onClick={() => p.caseId && navigate(`/casos/${p.caseId}`)}
                  className="p-3.5 rounded-xl border cursor-pointer hover:bg-background-50 transition-colors shadow-2xs hover:shadow-xs"
                  style={{ borderColor: overdue ? "rgb(254 202 202)" : p.daysRemaining <= 3 ? "rgb(253 230 138)" : "rgb(229 231 235)" }}
                >
                  <div className="flex items-center justify-between mb-1 gap-2">
                    <span className="text-xs font-semibold text-foreground-900 truncate">{p.title}</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${
                      overdue ? "bg-red-100 text-red-700 animate-pulse" : p.daysRemaining <= 3 ? "bg-amber-100 text-amber-700" : "bg-background-100 text-foreground-500"
                    }`}>
                      {overdue ? `Vencido ${Math.abs(p.daysRemaining)}d` : `${p.daysRemaining}d`}
                    </span>
                  </div>
                  <p className="text-xs text-foreground-600 font-medium">{p.studentName}</p>
                  <p className="text-[10px] text-foreground-400 mt-1 flex items-center gap-1">
                    <i className="ri-calendar-line" /> Límite: {p.fechaLimite}
                  </p>
                </div>
              );
            })}
            {plazos.length === 0 && (
              <p className="text-sm text-foreground-400 text-center py-8">No hay plazos de vencimiento activos</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}